import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { MapPin, Clock } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

const sectionBackground = `${process.env.PUBLIC_URL || ''}/background/9.png`;

interface EventCardProps {
  title: string;
  time: string;
  description: string;
  imageUrl: string;
  mapUrl: string;
  index: number;
}

function EventCard({ title, time, description, imageUrl, mapUrl, index }: EventCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ delay: index * 0.15, duration: 0.8 }}
      className="group relative"
    >
      <div
        className="relative rounded-xl overflow-hidden border bg-[var(--butter)]"
        style={{ borderColor: 'rgba(186, 159, 56, 0.2)' }}
      >
        <div className="relative h-64 md:h-80 overflow-hidden">
          <div
            className="absolute inset-0 z-10"
            style={{
              background: 'linear-gradient(to top, rgba(29, 59, 40, 0.4), transparent 40%)',
            }}
          />
          <ImageWithFallback
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
            fallbackText={title}
          />
          <div
            className="absolute top-4 right-4 z-20 px-3 py-1.5 rounded-full flex items-center gap-2 border"
            style={{
              backgroundColor: 'var(--butter)',
              borderColor: 'rgba(186, 159, 56, 0.3)',
            }}
          >
            <Clock className="w-4 h-4" style={{ color: 'var(--chartreuse)' }} />
            <span className="text-editorial-meta text-xs" style={{ color: 'var(--emerald)' }}>
              {time}
            </span>
          </div>
        </div>

        <div className="p-8">
          <h3
            className="text-editorial-title text-xl md:text-2xl tracking-[0.15em] mb-4"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            {title.toUpperCase()}
          </h3>
          <p
            className="mb-6 leading-relaxed text-sm"
            style={{ color: 'var(--emerald)', opacity: 0.8 }}
          >
            {description}
          </p>
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border transition-colors duration-300 hover:bg-[var(--pale-lime)]/40"
            style={{
              borderColor: 'var(--chartreuse)',
              color: 'var(--emerald)',
              fontFamily: 'var(--font-serif)',
              fontSize: '0.875rem',
            }}
          >
            <MapPin className="w-4 h-4" />
            <span>Ver ubicación</span>
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export function EventCards() {
  const [revealProgress, setRevealProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const inView = rect.top < vh && rect.bottom > 0;
      if (!inView && rect.top > vh) {
        setRevealProgress(0);
        return;
      }
      const visible = Math.min(1, 1 - rect.top / (vh * 0.6));
      setRevealProgress(Math.max(0, visible));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const events = [
    {
      title: 'Ceremonia',
      time: '16:45 hrs',
      description:
        'Acompáñanos en este momento amoroso donde nos daremos el "Sí, acepto" con la bendición de Dios.',
      imageUrl: `${process.env.PUBLIC_URL || ''}/background/ceremony.jpg`,
      mapUrl: 'https://maps.google.com/?q=Dreams+Jade+Resort+Cancun',
    },
    {
      title: 'Coctel en playa',
      time: '18:00 hrs',
      description:
        'Brindemos juntos al atardecer. Te esperamos en la playa para un coctel antes de la fiesta.',
      imageUrl: `${process.env.PUBLIC_URL || ''}/background/cocktail.jpg`,
      mapUrl: 'https://maps.google.com/?q=Dreams+Jade+Resort+Cancun',
    },
    {
      title: 'Recepción',
      time: '19:00 hrs',
      description:
        '¡Es hora de celebrar! Únete a nosotros para una noche inolvidable de música, baile y alegría frente al mar y bajo el cielo estrellado de Cancún.',
      imageUrl: `${process.env.PUBLIC_URL || ''}/background/reception.jpg`,
      mapUrl: 'https://maps.google.com/?q=Dreams+Jade+Resort+Cancun',
    }
    /*
    {
      title: 'Postboda / After Party',
      time: '23:00 hrs',
      description:
        'Nos vemos en el antro del hotel para celebrar. ¡Seguimos la fiesta!',
      imageUrl: `${process.env.PUBLIC_URL || ''}/background/afterparty.jpg`,
      mapUrl: 'https://maps.google.com/?q=Dreams+Jade+Resort+Cancun',
    },*/
  ];

  return (
    <section
      ref={sectionRef}
      className="snap-section relative min-h-screen overflow-hidden flex items-center justify-center"
    >
      <div className="section-content w-full py-24 px-6 min-h-screen flex flex-col justify-center">
        <div
          className="section-bg-image absolute inset-0 z-0"
          style={{ backgroundImage: `url(${sectionBackground})` }}
          aria-hidden
        />
        <div className="absolute inset-0 z-[1]" style={{ backgroundColor: 'rgba(252, 235, 208, 0.85)' }} />
        <div
          className="relative z-10 max-w-5xl mx-auto transition-all duration-500 ease-out"
          style={{
            opacity: revealProgress,
            transform: `translateY(${32 * (1 - revealProgress)}px)`,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-editorial-title text-2xl md:text-3xl tracking-[0.3em] mb-4">
              EL GRAN DÍA
            </h2>
            <div className="w-20 h-px mx-auto" style={{ backgroundColor: 'var(--chartreuse)' }} />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10 md:gap-12">
            {events.map((event, index) => (
              <EventCard key={event.title} {...event} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
